/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Tabs, Button, Form } from 'antd';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import FormObserver from 'components/New/FormStepper/FormObserver';
import { STATUS_ICONS, SUB_FORMS } from './constants';
import classes from './GenerateGCC.module.scss';
import { FormGenerateGccCpcDoc } from './context/FormGenerateGccCpcDoc';

const FormGenerateGCC = (props, ref) => {
  const {
    type = 'gcc',
    state,
    testDataResource,
    countryId,
    complianceDataUrl,
    initialValues = {},
    onSubmit,
    onErrors,
  } = props;
  const SubForms = React.useMemo(() => {
    return SUB_FORMS.filter((subForm) => subForm.isApplicable({ type }));
  }, [type]);
  const formikParentRef = React.useRef();
  const subFormRefs = React.useRef(
    new Map([
      ...SubForms.map((subForm) => {
        return [subForm.value, React.createRef()];
      }),
    ])
  ).current;
  const [serviceProviderOptions, setServiceProviderOptions] = React.useState([]);
  const [shouldValidateAllForm, setShouldValidateAllForm] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState(SubForms[0].value);
  const [statuses, setStatuses] = React.useState({
    general: 'default',
    testData: 'default',
    laboratory: 'default',
    notifiedBodyInformation: 'default',
  });

  const handleSubFormChange = (subFormName) => (values) => {
    if (type !== 'doc' && subFormName === 'testData') {
      const { laboratory = [] } = subFormRefs.get('laboratory').current.values;
      if (Array.isArray(values)) {
        const testData = values
          .map(({ serviceProvider }) => serviceProvider)
          .filter(({ name }) => !!name)
          .map((data) => ({
            ...data,
            phoneObj: {
              code: '+852',
              phone: null,
            },
          }));
        const testDataDelete = laboratory
          .filter(({ name }) => !testData.some((data) => data.name === name))
          .map(({ name }) => name);

        const testDataAdd = testData.filter(
          ({ name }) => !laboratory.some((data) => data.name === name)
        );
        const next = laboratory
          .filter(({ name }) => !testDataDelete.includes(name))
          .concat(testDataAdd);
        subFormRefs
          .get('laboratory')
          .current.setFieldValue('laboratory', next, true)
          .then((errors) => {
            if (!isEmpty(errors)) {
              setStatuses((prev) => {
                return {
                  ...prev,
                  laboratory: 'error',
                };
              });
            }
          });
      }
    }
    if (type === 'doc' && subFormName === 'testData') {
      const testDataHaveServiceProvider = (values || []).filter((data) => data.serviceProvider);

      const uniqueServiceProviders = [
        ...new Map(
          testDataHaveServiceProvider.map((data) => [
            data.serviceProvider.name,
            {
              label: data.serviceProvider.name,
              value: data.serviceProvider.name,
            },
          ])
        ).values(),
      ];

      setServiceProviderOptions(uniqueServiceProviders);
    }
  };

  const getSubFormEntries = () => {
    return Array.from(subFormRefs);
  };

  const handleValidateAllSubForms = async () => {
    let hasError = false;
    let returnError = {};
    const validateTasks = getSubFormEntries().map(([subFormName, subFormRef]) => {
      if (subFormRef.current) {
        return subFormRef.current.validateForm().then((errors) => {
          if (isEmpty(errors)) {
            setStatuses((prev) => {
              return {
                ...prev,
                [subFormName]: 'success',
              };
            });
          } else {
            hasError = true;
            returnError = {
              ...returnError,
              [subFormName]: errors[subFormName],
            };
            setStatuses((prev) => {
              return {
                ...prev,
                [subFormName]: 'error',
              };
            });
          }
        });
      }
      return Promise.resolve(true);
    });
    await Promise.all(validateTasks);
    return {
      hasError,
      errors: returnError,
    };
  };

  const handleValidateTabChange = async () => {
    let formErrors = {};
    const validateTasks = getSubFormEntries().map(([subFormName, subFormRef]) => {
      if (subFormRef.current) {
        return subFormRef.current.validateForm().then((errors) => {
          if (!isEmpty(errors)) {
            formErrors = {
              ...formErrors,
              [subFormName]: {
                ...errors,
              },
            };
          }
        });
      }
      return Promise.resolve(true);
    });
    await Promise.all(validateTasks);
    if (onErrors) {
      if (shouldValidateAllForm) {
        onErrors(formErrors);
      }
    }
    return formErrors;
  };

  const handleOnTabChange = async (key) => {
    if (subFormRefs.get(currentTab).current) {
      const errors = await handleValidateTabChange();
      const currentIndex = SubForms.findIndex((subForm) => subForm.value === currentTab);
      const nextIndex = SubForms.findIndex((subForm) => subForm.value === key);
      const tabStatus = isEmpty(errors[currentTab]) ? 'success' : 'error';

      setStatuses((prev) => {
        return {
          ...prev,
          [currentTab]: tabStatus,
        };
      });
      if (nextIndex < currentIndex) {
        setCurrentTab(key);
      }
      if (tabStatus === 'error') return;
      setCurrentTab(key);
    }
  };

  React.useImperativeHandle(ref, () => ({
    next: async () => {
      setShouldValidateAllForm(true);
      const { hasError, errors } = await handleValidateAllSubForms();

      const currentStep = SubForms.findIndex((subForm) => subForm.value === currentTab);
      const nextStep = SubForms[currentStep + 1];
      if (hasError && onErrors) {
        onErrors(errors);
      }
      if (errors[currentTab]) return;
      if (currentStep >= SubForms.length - 1) {
        formikParentRef.current.submitForm();
        return;
      }
      setCurrentTab(nextStep.value);
    },
    save: () => {
      setShouldValidateAllForm(false);
      onErrors({});
      onSubmit(formikParentRef.current.values, false);
    },
    submit: () => {},
    validate: () => {},
  }));

  React.useEffect(() => {
    if (state === 'edit' || state === 'view') {
      setShouldValidateAllForm(true);
      handleValidateAllSubForms().then(({ errors }) => {
        if (onErrors) {
          onErrors(errors);
        }
      });
    }
  }, [state]);

  React.useEffect(() => {
    if (type === 'doc') {
      const { testData = [] } = initialValues || {};
      const testDataHaveServiceProvider = (testData || []).filter((data) => data.serviceProvider);

      const uniqueServiceProviders = [
        ...new Map(
          testDataHaveServiceProvider.map((data) => [
            data.serviceProvider.name,
            {
              label: data.serviceProvider.name,
              value: data.serviceProvider.name,
            },
          ])
        ).values(),
      ];

      setServiceProviderOptions(uniqueServiceProviders);
    }
  }, [initialValues]);

  return (
    <FormGenerateGccCpcDoc.Provider
      value={{
        type,
        serviceProviderOptions,
        complianceDataUrl,
      }}>
      <div className={classes.generateForm}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={(formValues) => {
            onSubmit(formValues, true);
          }}>
          {(formikProps) => {
            formikParentRef.current = formikProps;
            return (
              <div>
                <Tabs
                  className={classes.tabs}
                  animated={{
                    inkBar: true,
                    tabPane: true,
                  }}
                  activeKey={currentTab}
                  renderTabBar={(tabProps) => {
                    return (
                      <div {...tabProps} className={classes.tabBar}>
                        {SubForms.map((subForm) => {
                          const StatusIcon = STATUS_ICONS[statuses[subForm.value]];
                          return (
                            <Button
                              className={cn({
                                [classes.active]: currentTab === subForm.value,
                              })}
                              size="large"
                              key={subForm.value}
                              type={currentTab === subForm.value ? 'primary' : 'default'}
                              onClick={() => {
                                handleOnTabChange(subForm.value);
                              }}>
                              {subForm.label}
                              {StatusIcon}
                            </Button>
                          );
                        })}
                      </div>
                    );
                  }}>
                  {SubForms.map((subForm) => {
                    return (
                      <Tabs.TabPane key={subForm.value} tab={subForm.label} forceRender>
                        <Formik
                          enableReinitialize
                          validationSchema={
                            subForm.validateSchema ? subForm.validateSchema(type) : null
                          }
                          initialValues={{
                            [subForm.value]: initialValues[subForm.value],
                          }}
                          onSubmit={() => {}}>
                          {(formikSubFormProps) => {
                            subFormRefs.get(subForm.value).current = formikSubFormProps;
                            return (
                              <Form layout="vertical">
                                <FormObserver
                                  onChange={(_, values) => {
                                    formikProps.setFieldValue(subForm.value, values[subForm.value]);
                                    handleSubFormChange(subForm.value)(values[subForm.value]);
                                  }}
                                  onValidateChange={async () => {
                                    if (subForm.value === currentTab || state === 'edit') {
                                      const errors = await handleValidateTabChange();
                                      const subFormErrors = errors[subForm.value] || {};
                                      formikSubFormProps.setTouched(subFormErrors);
                                      setStatuses((prev) => {
                                        return {
                                          ...prev,
                                          [subForm.value]: isEmpty(subFormErrors)
                                            ? 'success'
                                            : 'error',
                                        };
                                      });
                                    }
                                  }}
                                />
                                <div className={classes.content}>
                                  {React.cloneElement(subForm.children, {
                                    exportMarketId: countryId,
                                    resource: testDataResource,
                                  })}
                                </div>
                              </Form>
                            );
                          }}
                        </Formik>
                      </Tabs.TabPane>
                    );
                  })}
                </Tabs>
              </div>
            );
          }}
        </Formik>
      </div>
    </FormGenerateGccCpcDoc.Provider>
  );
};

FormGenerateGCC.defaultProps = {
  state: ['new'],
  countryId: null,
  onErrors: null,
};

FormGenerateGCC.propTypes = {
  onErrors: PropTypes.func,
  type: PropTypes.oneOf(['gcc', 'cpc', 'doc']).isRequired,
  state: PropTypes.oneOf(['create', 'edit', 'view']),
  initialValues: PropTypes.objectOf({
    general: PropTypes.objectOf({
      productName: PropTypes.string.isRequired,
      productModel: PropTypes.string.isRequired,
      companyName: PropTypes.string.isRequired,
      companyPhone: PropTypes.objectOf({
        phone: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      }),
      companyAddress: PropTypes.string.isRequired,
      contactName: PropTypes.string.isRequired,
      contactAddress: PropTypes.string.isRequired,
      contactPhone: PropTypes.objectOf({
        phone: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      }),
      contactEmail: PropTypes.string.isRequired,
      manufactureDate: PropTypes.string.isRequired,
      manufacturePlace: PropTypes.string.isRequired,
    }).isRequired,
    testData: PropTypes.arrayOf(
      PropTypes.objectOf({
        testItemId: PropTypes.mixed,
        citationId: PropTypes.mixed,
        serviceProvider: PropTypes.objectOf({
          name: PropTypes.string.isRequired,
        }),
        expiryDate: PropTypes.string,
        reportId: PropTypes.string,
      })
    ).isRequired,
    laboratory: PropTypes.arrayOf(
      PropTypes.objectOf({
        phoneObj: PropTypes.objectOf({
          phone: PropTypes.string,
          code: PropTypes.string,
        }),
        address: PropTypes.string,
        area: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  countryId: PropTypes.string,
  testDataResource: PropTypes.objectOf({
    name: PropTypes.string.isRequired,
  }).isRequired,
  //   actions: PropTypes.arrayOf(PropTypes.string),
};

export default React.forwardRef(FormGenerateGCC);
